import * as React from 'react';

import {colors} from '../colors/colors';

import {RANKED_COLORS} from './consts';
import UsaMapSvg from './usa-map.svg';

import styles from './usa-map.module.css';

const X_POS_TOOLTIP_OFFSET = 140;
const Y_POS_TOOLTIP_OFFSET = 35;

type HoveredState = {
    position: {
        x: number;
        y: number;
    };
    stateId: string;
};

type Props = {
    rankedStateIds: string[];
    getHoverTooltipComponent: (stateId: string) => React.ReactElement;
};

export function UsaMap(props: Props) {
    const {getHoverTooltipComponent} = props;

    const [hoveredState, setHoveredState] = React.useState<HoveredState | null | undefined>(
        undefined
    );

    const mapContainerRef = React.useRef(undefined);

    const rankedStateIds = React.useMemo(() => props.rankedStateIds, [props.rankedStateIds]);

    React.useLayoutEffect(() => {
        if (mapContainerRef && mapContainerRef.current) {
            const containerRect = mapContainerRef.current // Flow blows up if we don't check this again
                ? // @ts-expect-error
                  mapContainerRef.current.getBoundingClientRect()
                : {width: 0, height: 0};

            // @ts-expect-error
            Array.from(mapContainerRef.current.getElementsByTagName('path')).forEach((element) => {
                // @ts-expect-error
                const pathId = element.getAttribute('data-id');
                const statePos = rankedStateIds.findIndex((stateId) => pathId === stateId);

                if (statePos !== -1 && statePos < RANKED_COLORS.length && pathId) {
                    // Fill path with corresponding color
                    // @ts-expect-error
                    element.style.setProperty('fill', colors[RANKED_COLORS[statePos]]);

                    // Set onMouseEnter effect
                    // @ts-expect-error
                    element.onmouseenter = (ev: MouseEvent) => {
                        // @ts-expect-error
                        element.style.setProperty('opacity', '0.8');

                        // Default tooltip X position 8px to right of cursor
                        let xPos = ev.offsetX + 8;
                        // Default tooltip Y position 25px to top of cursor (to appear centered)
                        let yPos = ev.offsetY - Y_POS_TOOLTIP_OFFSET;

                        // Check if not enough room for tooltip to the right of cursor
                        if (containerRect.width - ev.offsetX <= X_POS_TOOLTIP_OFFSET) {
                            // Set tooltip position to the left of cursor
                            xPos = ev.offsetX - X_POS_TOOLTIP_OFFSET;
                        }

                        // Check if enough room for tooltip at bottom of cursor
                        if (containerRect.height - ev.offsetY <= Y_POS_TOOLTIP_OFFSET) {
                            // Raise tooltip to be even with cursor
                            yPos = yPos - Y_POS_TOOLTIP_OFFSET;
                        }

                        setHoveredState({
                            position: {
                                x: xPos,
                                y: yPos < 0 ? 0 : yPos,
                            },
                            stateId: pathId,
                        });
                    };

                    // Set onMouseLeave effect
                    // @ts-expect-error
                    element.onmouseleave = () => {
                        // @ts-expect-error
                        element.style.removeProperty('opacity');
                        setHoveredState(undefined);
                    };
                }
            });
        }
    }, [rankedStateIds]);

    return (
        <div
            ref={(c) => {
                // @ts-expect-error
                mapContainerRef.current = c;
            }}
            className={styles['usa-map-container']}
        >
            <UsaMapSvg />
            {hoveredState ? (
                <div
                    style={{
                        left: `${hoveredState.position.x}px`,
                        top: `${hoveredState.position.y}px`,
                    }}
                    className={styles['hover-container']}
                >
                    {getHoverTooltipComponent(hoveredState.stateId)}
                </div>
            ) : undefined}
        </div>
    );
}
